import React, { useState, useEffect } from 'react';
import { cryptoAPI } from '../services/api';

const CryptoManagement = () => {
  // State management
  const [activeTab, setActiveTab] = useState('transactions');
  const [transactions, setTransactions] = useState([]);
  const [wallets, setWallets] = useState([]);
  const [statistics, setStatistics] = useState({});
  const [statuses, setStatuses] = useState({});
  const [types, setTypes] = useState({});
  const [currencies, setCurrencies] = useState({});
  const [walletStatuses, setWalletStatuses] = useState({});
  const [users, setUsers] = useState([]);
  const [walletsDropdown, setWalletsDropdown] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  
  // Modal state
  const [selectedItem, setSelectedItem] = useState(null);
  const [showViewModal, setShowViewModal] = useState(false);
  const [showEditModal, setShowEditModal] = useState(false);
  const [showCreateModal, setShowCreateModal] = useState(false);
  const [showDeleteModal, setShowDeleteModal] = useState(false);
  
  // Filter state
  const [filters, setFilters] = useState({
    search: '',
    status: '',
    type: '',
    currency: '',
    min_amount: '',
    max_amount: '',
    from_date: '',
    to_date: '',
    per_page: 20,
    page: 1
  });

  // Debounced search
  const [searchInput, setSearchInput] = useState('');

  // Load initial data
  useEffect(() => {
    loadInitialData();
  }, []);

  // Handle search debouncing
  useEffect(() => {
    const timeoutId = setTimeout(() => {
      setFilters(prev => ({ ...prev, search: searchInput, page: 1 }));
    }, 500);
    return () => clearTimeout(timeoutId);
  }, [searchInput]);

  // Load data when filters change
  useEffect(() => {
    if (activeTab === 'transactions') {
      loadTransactions();
    } else {
      loadWallets();
    }
  }, [filters, activeTab]);

  const loadInitialData = async () => {
    try {
      setLoading(true);
      const [statsRes, statusesRes, typesRes, currenciesRes, walletStatusesRes, usersRes, walletsRes] = await Promise.all([
        cryptoAPI.getStatistics(),
        cryptoAPI.getStatuses(),
        cryptoAPI.getTypes(),
        cryptoAPI.getCurrencies(),
        cryptoAPI.getWalletStatuses(),
        cryptoAPI.getUsers(),
        cryptoAPI.getWalletsDropdown()
      ]);
      
      setStatistics(statsRes.data);
      setStatuses(statusesRes.data);
      setTypes(typesRes.data);
      setCurrencies(currenciesRes.data);
      setWalletStatuses(walletStatusesRes.data);
      setUsers(usersRes.data);
      setWalletsDropdown(walletsRes.data);
    } catch (err) {
      setError('Failed to load initial data');
      console.error('Initial data loading error:', err);
    } finally {
      setLoading(false);
    }
  };

  const loadTransactions = async () => {
    try {
      setLoading(true);
      const response = await cryptoAPI.getTransactions(filters);
      setTransactions(response.data);
    } catch (err) {
      setError('Failed to load transactions');
      console.error('Transactions loading error:', err);
    } finally {
      setLoading(false);
    }
  };

  const loadWallets = async () => {
    try {
      setLoading(true);
      const response = await cryptoAPI.getWallets(filters);
      setWallets(response.data);
    } catch (err) {
      setError('Failed to load wallets');
      console.error('Wallets loading error:', err);
    } finally {
      setLoading(false);
    }
  };

  const handleFilterChange = (key, value) => {
    setFilters(prev => ({ ...prev, [key]: value, page: 1 }));
  };

  const clearFilters = () => {
    setSearchInput('');
    setFilters({
      search: '',
      status: '',
      type: '',
      currency: '',
      min_amount: '',
      max_amount: '',
      from_date: '',
      to_date: '',
      per_page: 20,
      page: 1
    });
  };

  const handleViewItem = async (itemId) => {
    try {
      const response = activeTab === 'transactions' 
        ? await cryptoAPI.getTransaction(itemId)
        : await cryptoAPI.getWallet(itemId);
      setSelectedItem(response.data);
      setShowViewModal(true);
    } catch (err) {
      setError(`Failed to load ${activeTab.slice(0, -1)} details`);
    }
  };

  const handleEditItem = async (itemId) => {
    try {
      const response = activeTab === 'transactions' 
        ? await cryptoAPI.getTransaction(itemId)
        : await cryptoAPI.getWallet(itemId);
      setSelectedItem(response.data);
      setShowEditModal(true);
    } catch (err) {
      setError(`Failed to load ${activeTab.slice(0, -1)} details`);
    }
  };

  const handleDeleteItem = (item) => {
    setSelectedItem(item);
    setShowDeleteModal(true);
  };

  const handleCreateTransaction = () => {
    setShowCreateModal(true);
  };

  const handleCreateWallet = () => {
    setShowCreateModal(true);
  };

  const executeDeleteItem = async () => {
    try {
      if (activeTab === 'transactions') {
        await cryptoAPI.deleteTransaction(selectedItem.id);
        await loadTransactions();
      } else {
        await cryptoAPI.deleteWallet(selectedItem.id);
        await loadWallets();
      }
      await loadInitialData(); // Refresh statistics
      setShowDeleteModal(false);
      setSelectedItem(null);
      setError(null);
    } catch (err) {
      setError(`Failed to delete ${activeTab.slice(0, -1)}: ` + (err.response?.data?.message || err.message));
    }
  };

  const formatCurrency = (amount) => {
    return new Intl.NumberFormat('en-US', {
      style: 'currency',
      currency: 'USD'
    }).format(amount);
  };

  const formatDate = (dateString) => {
    return new Date(dateString).toLocaleDateString('en-US', {
      year: 'numeric',
      month: 'short',
      day: 'numeric',
      hour: '2-digit',
      minute: '2-digit'
    });
  };

  const getStatusBadge = (status) => {
    const statusColors = {
      pending: 'bg-yellow-100 text-yellow-800',
      processing: 'bg-blue-100 text-blue-800',
      completed: 'bg-green-100 text-green-800',
      failed: 'bg-red-100 text-red-800',
      cancelled: 'bg-gray-100 text-gray-800',
      active: 'bg-green-100 text-green-800',
      suspended: 'bg-yellow-100 text-yellow-800',
      closed: 'bg-red-100 text-red-800'
    };
    
    return (
      <span className={`px-2 py-1 text-xs font-medium rounded-full ${statusColors[status] || 'bg-gray-100 text-gray-800'}`}>
        {activeTab === 'transactions' ? (statuses[status] || status) : (walletStatuses[status] || status)}
      </span>
    );
  };

  const getTypeBadge = (type) => {
    const typeColors = {
      buy: 'bg-green-100 text-green-800',
      sell: 'bg-red-100 text-red-800'
    };
    
    return (
      <span className={`px-2 py-1 text-xs font-medium rounded-full ${typeColors[type] || 'bg-gray-100 text-gray-800'}`}>
        {types[type] || type}
      </span>
    );
  };

  if (loading && !transactions.data && !wallets.data) {
    return (
      <div className="flex justify-center items-center h-64">
        <div className="animate-spin rounded-full h-32 w-32 border-b-2 border-blue-500"></div>
      </div>
    );
  }

  return (
    <div className="p-6 bg-gray-50 min-h-screen">
      <div className="max-w-7xl mx-auto">
        {/* Header */}
        <div className="mb-8">
          <h1 className="text-3xl font-bold text-gray-900">Crypto Management</h1>
          <p className="mt-2 text-sm text-gray-600">
            Monitor and manage cryptocurrency transactions and wallets
          </p>
        </div>

        {/* Error Display */}
        {error && (
          <div className="mb-6 bg-red-50 border border-red-200 text-red-700 px-4 py-3 rounded relative">
            <span className="block sm:inline">{error}</span>
            <span 
              className="absolute top-0 bottom-0 right-0 px-4 py-3 cursor-pointer"
              onClick={() => setError(null)}
            >
              <svg className="fill-current h-6 w-6 text-red-500" role="button" xmlns="http://www.w3.org/2000/svg" viewBox="0 0 20 20">
                <title>Close</title>
                <path d="M14.348 14.849a1.2 1.2 0 0 1-1.697 0L10 11.819l-2.651 3.029a1.2 1.2 0 1 1-1.697-1.697l2.758-3.15-2.759-3.152a1.2 1.2 0 1 1 1.697-1.697L10 8.183l2.651-3.031a1.2 1.2 0 1 1 1.697 1.697l-2.758 3.152 2.758 3.15a1.2 1.2 0 0 1 0 1.698z"/>
              </svg>
            </span>
          </div>
        )}

        {/* Statistics Cards */}
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-6 mb-8">
          <div className="bg-white overflow-hidden shadow rounded-lg">
            <div className="p-5">
              <div className="flex items-center">
                <div className="flex-shrink-0">
                  <div className="w-8 h-8 bg-blue-500 rounded-md flex items-center justify-center">
                    <span className="text-white text-sm font-medium">₿</span>
                  </div>
                </div>
                <div className="ml-5 w-0 flex-1">
                  <dl>
                    <dt className="text-sm font-medium text-gray-500 truncate">Total Transactions</dt>
                    <dd className="text-lg font-medium text-gray-900">
                      {statistics.total_transactions || 0}
                    </dd>
                  </dl>
                </div>
              </div>
            </div>
          </div>

          <div className="bg-white overflow-hidden shadow rounded-lg">
            <div className="p-5">
              <div className="flex items-center">
                <div className="flex-shrink-0">
                  <div className="w-8 h-8 bg-green-500 rounded-md flex items-center justify-center">
                    <span className="text-white text-sm font-medium">👛</span>
                  </div>
                </div>
                <div className="ml-5 w-0 flex-1">
                  <dl>
                    <dt className="text-sm font-medium text-gray-500 truncate">Active Wallets</dt>
                    <dd className="text-lg font-medium text-gray-900">
                      {statistics.active_wallets || 0}
                    </dd>
                  </dl>
                </div>
              </div>
            </div>
          </div>

          <div className="bg-white overflow-hidden shadow rounded-lg">
            <div className="p-5">
              <div className="flex items-center">
                <div className="flex-shrink-0">
                  <div className="w-8 h-8 bg-yellow-500 rounded-md flex items-center justify-center">
                    <span className="text-white text-sm font-medium">⏳</span>
                  </div>
                </div>
                <div className="ml-5 w-0 flex-1">
                  <dl>
                    <dt className="text-sm font-medium text-gray-500 truncate">Pending</dt>
                    <dd className="text-lg font-medium text-gray-900">
                      {statistics.pending_transactions || 0}
                    </dd>
                  </dl>
                </div>
              </div>
            </div>
          </div>

          <div className="bg-white overflow-hidden shadow rounded-lg">
            <div className="p-5">
              <div className="flex items-center">
                <div className="flex-shrink-0">
                  <div className="w-8 h-8 bg-purple-500 rounded-md flex items-center justify-center">
                    <span className="text-white text-sm font-medium">💰</span>
                  </div>
                </div>
                <div className="ml-5 w-0 flex-1">
                  <dl>
                    <dt className="text-sm font-medium text-gray-500 truncate">Monthly Volume</dt>
                    <dd className="text-lg font-medium text-gray-900">
                      {formatCurrency(statistics.total_volume_month || 0)}
                    </dd>
                  </dl>
                </div>
              </div>
            </div>
          </div>
        </div>

        {/* Tabs */}
        <div className="mb-8">
          <div className="border-b border-gray-200">
            <nav className="-mb-px flex space-x-8">
              <button
                onClick={() => setActiveTab('transactions')}
                className={`py-2 px-1 border-b-2 font-medium text-sm ${
                  activeTab === 'transactions'
                    ? 'border-blue-500 text-blue-600'
                    : 'border-transparent text-gray-500 hover:text-gray-700 hover:border-gray-300'
                }`}
              >
                Transactions ({statistics.total_transactions || 0})
              </button>
              <button
                onClick={() => setActiveTab('wallets')}
                className={`py-2 px-1 border-b-2 font-medium text-sm ${
                  activeTab === 'wallets'
                    ? 'border-blue-500 text-blue-600'
                    : 'border-transparent text-gray-500 hover:text-gray-700 hover:border-gray-300'
                }`}
              >
                Wallets ({statistics.total_wallets || 0})
              </button>
            </nav>
          </div>
        </div>

        {/* Filters */}
        <div className="bg-white shadow rounded-lg mb-8">
          <div className="px-6 py-4 border-b border-gray-200">
            <h3 className="text-lg font-medium text-gray-900">Filters</h3>
          </div>
          <div className="p-6">
            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-4">
              <div>
                <label className="block text-sm font-medium text-gray-700">Search</label>
                <input
                  type="text"
                  value={searchInput}
                  onChange={(e) => setSearchInput(e.target.value)}
                  placeholder={activeTab === 'transactions' ? "Transaction hash, user..." : "Currency, address, user..."}
                  className="mt-1 block w-full border-gray-300 rounded-md shadow-sm focus:ring-blue-500 focus:border-blue-500 sm:text-sm"
                />
              </div>

              <div>
                <label className="block text-sm font-medium text-gray-700">Status</label>
                <select
                  value={filters.status}
                  onChange={(e) => handleFilterChange('status', e.target.value)}
                  className="mt-1 block w-full border-gray-300 rounded-md shadow-sm focus:ring-blue-500 focus:border-blue-500 sm:text-sm"
                >
                  <option value="">All Statuses</option>
                  {Object.entries(activeTab === 'transactions' ? statuses : walletStatuses).map(([key, value]) => (
                    <option key={key} value={key}>{value}</option>
                  ))}
                </select>
              </div>

              {activeTab === 'transactions' && (
                <div>
                  <label className="block text-sm font-medium text-gray-700">Type</label>
                  <select
                    value={filters.type}
                    onChange={(e) => handleFilterChange('type', e.target.value)}
                    className="mt-1 block w-full border-gray-300 rounded-md shadow-sm focus:ring-blue-500 focus:border-blue-500 sm:text-sm"
                  >
                    <option value="">All Types</option>
                    {Object.entries(types).map(([key, value]) => (
                      <option key={key} value={key}>{value}</option>
                    ))}
                  </select>
                </div>
              )}

              <div>
                <label className="block text-sm font-medium text-gray-700">Currency</label>
                <select
                  value={filters.currency}
                  onChange={(e) => handleFilterChange('currency', e.target.value)}
                  className="mt-1 block w-full border-gray-300 rounded-md shadow-sm focus:ring-blue-500 focus:border-blue-500 sm:text-sm"
                >
                  <option value="">All Currencies</option>
                  {Object.entries(currencies).map(([key, value]) => (
                    <option key={key} value={key}>{value}</option>
                  ))}
                </select>
              </div>

              <div className="flex items-end">
                <button
                  onClick={clearFilters}
                  className="w-full px-3 py-2 border border-gray-300 rounded-md text-sm font-medium text-gray-700 bg-white hover:bg-gray-50 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-blue-500"
                >
                  Clear Filters
                </button>
              </div>
            </div>
          </div>
        </div>

        {/* Content */}
        {activeTab === 'transactions' ? (
          <TransactionsTable 
            transactions={transactions}
            loading={loading}
            formatCurrency={formatCurrency}
            formatDate={formatDate}
            getStatusBadge={getStatusBadge}
            getTypeBadge={getTypeBadge}
            currencies={currencies}
            onView={handleViewItem}
            onEdit={handleEditItem}
            onDelete={handleDeleteItem}
            onCreate={handleCreateTransaction}
            handleFilterChange={handleFilterChange}
          />
        ) : (
          <WalletsTable 
            wallets={wallets}
            loading={loading}
            formatDate={formatDate}
            getStatusBadge={getStatusBadge}
            currencies={currencies}
            onView={handleViewItem}
            onEdit={handleEditItem}
            onDelete={handleDeleteItem}
            onCreate={handleCreateWallet}
            handleFilterChange={handleFilterChange}
          />
        )}

        {/* Modals */}
        {showViewModal && selectedItem && (
          <ViewModal
            item={selectedItem}
            type={activeTab.slice(0, -1)}
            formatCurrency={formatCurrency}
            formatDate={formatDate}
            getStatusBadge={getStatusBadge}
            getTypeBadge={getTypeBadge}
            currencies={currencies}
            onClose={() => setShowViewModal(false)}
          />
        )}

        {showEditModal && selectedItem && (
          <EditModal
            item={selectedItem}
            type={activeTab.slice(0, -1)}
            statuses={activeTab === 'transactions' ? statuses : walletStatuses}
            types={types}
            currencies={currencies}
            users={users}
            wallets={walletsDropdown}
            onClose={() => setShowEditModal(false)}
            onSave={async (data) => {
              try {
                if (activeTab === 'transactions') {
                  await cryptoAPI.updateTransaction(selectedItem.id, data);
                  await loadTransactions();
                } else {
                  await cryptoAPI.updateWallet(selectedItem.id, data);
                  await loadWallets();
                }
                await loadInitialData();
                setShowEditModal(false);
                setSelectedItem(null);
                setError(null);
              } catch (err) {
                setError(`Failed to update ${activeTab.slice(0, -1)}: ` + (err.response?.data?.message || err.message));
              }
            }}
          />
        )}

        {showCreateModal && (
          activeTab === 'transactions' ? (
            <CreateTransactionModal
              users={users}
              wallets={walletsDropdown}
              statuses={statuses}
              types={types}
              currencies={currencies}
              onClose={() => setShowCreateModal(false)}
              onSave={async (data) => {
                try {
                  await cryptoAPI.createTransaction(data);
                  await loadTransactions();
                  await loadInitialData();
                  setShowCreateModal(false);
                  setError(null);
                } catch (err) {
                  setError('Failed to create transaction: ' + (err.response?.data?.message || err.message));
                }
              }}
            />
          ) : (
            <CreateWalletModal
              users={users}
              currencies={currencies}
              walletStatuses={walletStatuses}
              onClose={() => setShowCreateModal(false)}
              onSave={async (data) => {
                try {
                  await cryptoAPI.createWallet(data);
                  await loadWallets();
                  await loadInitialData();
                  setShowCreateModal(false);
                  setError(null);
                } catch (err) {
                  setError('Failed to create wallet: ' + (err.response?.data?.message || err.message));
                }
              }}
            />
          )
        )}

        {showDeleteModal && selectedItem && (
          <ConfirmationModal
            title={`Delete ${activeTab.slice(0, -1)}`}
            message={`Are you sure you want to delete this ${activeTab.slice(0, -1)}? This action cannot be undone.`}
            confirmText="Delete"
            confirmClass="bg-red-600 hover:bg-red-700"
            onConfirm={executeDeleteItem}
            onCancel={() => {
              setShowDeleteModal(false);
              setSelectedItem(null);
            }}
          />
        )}
      </div>
    </div>
  );
};

// Transactions Table Component
const TransactionsTable = ({ transactions, loading, formatCurrency, formatDate, getStatusBadge, getTypeBadge, currencies, onView, onEdit, onDelete, onCreate, handleFilterChange }) => (
  <div className="bg-white shadow overflow-hidden sm:rounded-md">
    <div className="px-6 py-4 border-b border-gray-200 flex justify-between items-center">
      <h3 className="text-lg font-medium text-gray-900">
        Crypto Transactions
        {transactions.total && (
          <span className="ml-2 text-sm text-gray-500">
            ({transactions.total} total)
          </span>
        )}
      </h3>
      <button
        onClick={onCreate}
        className="bg-blue-600 hover:bg-blue-700 text-white px-4 py-2 rounded-md text-sm font-medium"
      >
        Create New Transaction
      </button>
    </div>
    
    {loading ? (
      <div className="flex justify-center items-center py-12">
        <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-blue-500"></div>
      </div>
    ) : transactions.data && transactions.data.length > 0 ? (
      <>
        <div className="overflow-x-auto">
          <table className="min-w-full divide-y divide-gray-200">
            <thead className="bg-gray-50">
              <tr>
                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                  User
                </th>
                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                  Type
                </th>
                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                  Currency
                </th>
                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                  Amount
                </th>
                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                  USD Value
                </th>
                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                  Status
                </th>
                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                  Date
                </th>
                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                  Actions
                </th>
              </tr>
            </thead>
            <tbody className="bg-white divide-y divide-gray-200">
              {transactions.data.map((transaction) => (
                <tr key={transaction.id} className="hover:bg-gray-50">
                  <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-500">
                    <div>
                      <div className="font-medium">{transaction.user.name}</div>
                      <div className="text-gray-400">{transaction.user.email}</div>
                    </div>
                  </td>
                  <td className="px-6 py-4 whitespace-nowrap">
                    {getTypeBadge(transaction.type)}
                  </td>
                  <td className="px-6 py-4 whitespace-nowrap text-sm font-medium text-gray-900">
                    {transaction.currency_code}
                  </td>
                  <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-900">
                    {transaction.crypto_amount}
                  </td>
                  <td className="px-6 py-4 whitespace-nowrap text-sm font-medium text-gray-900">
                    {formatCurrency(transaction.usd_amount)}
                  </td>
                  <td className="px-6 py-4 whitespace-nowrap">
                    {getStatusBadge(transaction.status)}
                  </td>
                  <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-500">
                    {formatDate(transaction.created_at)}
                  </td>
                  <td className="px-6 py-4 whitespace-nowrap text-right text-sm font-medium">
                    <div className="flex space-x-2">
                      <button
                        onClick={() => onView(transaction.id)}
                        className="text-blue-600 hover:text-blue-900"
                        title="View Details"
                      >
                        View
                      </button>
                      <button
                        onClick={() => onEdit(transaction.id)}
                        className="text-indigo-600 hover:text-indigo-900"
                        title="Edit Transaction"
                      >
                        Edit
                      </button>
                      <button
                        onClick={() => onDelete(transaction)}
                        className="text-red-600 hover:text-red-900"
                        title="Delete Transaction"
                      >
                        Delete
                      </button>
                    </div>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
        {/* Pagination would go here */}
      </>
    ) : (
      <div className="text-center py-12">
        <div className="text-gray-500">
          <div className="text-4xl mb-4">₿</div>
          <h3 className="text-lg font-medium">No transactions found</h3>
          <p className="text-sm">Try adjusting your filters to see more results.</p>
        </div>
      </div>
    )}
  </div>
);

// Wallets Table Component
const WalletsTable = ({ wallets, loading, formatDate, getStatusBadge, currencies, onView, onEdit, onDelete, onCreate, handleFilterChange }) => (
  <div className="bg-white shadow overflow-hidden sm:rounded-md">
    <div className="px-6 py-4 border-b border-gray-200 flex justify-between items-center">
      <h3 className="text-lg font-medium text-gray-900">
        Crypto Wallets
        {wallets.total && (
          <span className="ml-2 text-sm text-gray-500">
            ({wallets.total} total)
          </span>
        )}
      </h3>
      <button
        onClick={onCreate}
        className="bg-blue-600 hover:bg-blue-700 text-white px-4 py-2 rounded-md text-sm font-medium"
      >
        Create New Wallet
      </button>
    </div>
    
    {loading ? (
      <div className="flex justify-center items-center py-12">
        <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-blue-500"></div>
      </div>
    ) : wallets.data && wallets.data.length > 0 ? (
      <>
        <div className="overflow-x-auto">
          <table className="min-w-full divide-y divide-gray-200">
            <thead className="bg-gray-50">
              <tr>
                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                  User
                </th>
                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                  Currency
                </th>
                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                  Balance
                </th>
                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                  Wallet Address
                </th>
                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                  Status
                </th>
                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                  Created Date
                </th>
                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                  Actions
                </th>
              </tr>
            </thead>
            <tbody className="bg-white divide-y divide-gray-200">
              {wallets.data.map((wallet) => (
                <tr key={wallet.id} className="hover:bg-gray-50">
                  <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-500">
                    <div>
                      <div className="font-medium">{wallet.user.name}</div>
                      <div className="text-gray-400">{wallet.user.email}</div>
                    </div>
                  </td>
                  <td className="px-6 py-4 whitespace-nowrap text-sm font-medium text-gray-900">
                    <div>
                      <div>{wallet.currency_name}</div>
                      <div className="text-gray-500">{wallet.currency_code}</div>
                    </div>
                  </td>
                  <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-900">
                    {wallet.balance}
                  </td>
                  <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-500">
                    <div className="max-w-xs truncate">
                      {wallet.wallet_address}
                    </div>
                  </td>
                  <td className="px-6 py-4 whitespace-nowrap">
                    {getStatusBadge(wallet.status)}
                  </td>
                  <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-500">
                    {formatDate(wallet.created_at)}
                  </td>
                  <td className="px-6 py-4 whitespace-nowrap text-right text-sm font-medium">
                    <div className="flex space-x-2">
                      <button
                        onClick={() => onView(wallet.id)}
                        className="text-blue-600 hover:text-blue-900"
                        title="View Details"
                      >
                        View
                      </button>
                      <button
                        onClick={() => onEdit(wallet.id)}
                        className="text-indigo-600 hover:text-indigo-900"
                        title="Edit Wallet"
                      >
                        Edit
                      </button>
                      <button
                        onClick={() => onDelete(wallet)}
                        className="text-red-600 hover:text-red-900"
                        title="Delete Wallet"
                      >
                        Delete
                      </button>
                    </div>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
        {/* Pagination would go here */}
      </>
    ) : (
      <div className="text-center py-12">
        <div className="text-gray-500">
          <div className="text-4xl mb-4">👛</div>
          <h3 className="text-lg font-medium">No wallets found</h3>
          <p className="text-sm">Try adjusting your filters to see more results.</p>
        </div>
      </div>
    )}
  </div>
);

// View Modal Component
const ViewModal = ({ item, type, formatCurrency, formatDate, getStatusBadge, getTypeBadge, currencies, onClose }) => (
  <div className="fixed inset-0 bg-gray-600 bg-opacity-50 overflow-y-auto h-full w-full z-50">
    <div className="relative top-20 mx-auto p-5 border w-96 shadow-lg rounded-md bg-white">
      <div className="mt-3">
        <div className="flex items-center justify-between mb-4">
          <h3 className="text-lg font-medium text-gray-900">{type === 'transaction' ? 'Transaction' : 'Wallet'} Details</h3>
          <button onClick={onClose} className="text-gray-400 hover:text-gray-600">
            <span className="sr-only">Close</span>
            <svg className="h-6 w-6" fill="none" viewBox="0 0 24 24" stroke="currentColor">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M6 18L18 6M6 6l12 12" />
            </svg>
          </button>
        </div>
        
        <div className="space-y-4">
          {type === 'transaction' ? (
            <>
              <div>
                <label className="block text-sm font-medium text-gray-700">Transaction Hash</label>
                <p className="mt-1 text-sm text-gray-900 break-all">{item.transaction_hash}</p>
              </div>
              
              <div>
                <label className="block text-sm font-medium text-gray-700">Type</label>
                <div className="mt-1">{getTypeBadge(item.type)}</div>
              </div>
              
              <div>
                <label className="block text-sm font-medium text-gray-700">Currency</label>
                <p className="mt-1 text-sm text-gray-900">{currencies[item.currency_code]} ({item.currency_code})</p>
              </div>
              
              <div>
                <label className="block text-sm font-medium text-gray-700">Crypto Amount</label>
                <p className="mt-1 text-sm text-gray-900">{item.crypto_amount} {item.currency_code}</p>
              </div>
              
              <div>
                <label className="block text-sm font-medium text-gray-700">USD Amount</label>
                <p className="mt-1 text-sm text-gray-900">{formatCurrency(item.usd_amount)}</p>
              </div>
              
              <div>
                <label className="block text-sm font-medium text-gray-700">Price per Unit</label>
                <p className="mt-1 text-sm text-gray-900">{formatCurrency(item.price_per_unit)}</p>
              </div>
              
              <div>
                <label className="block text-sm font-medium text-gray-700">Fee</label>
                <p className="mt-1 text-sm text-gray-900">{formatCurrency(item.fee)}</p>
              </div>
            </>
          ) : (
            <>
              <div>
                <label className="block text-sm font-medium text-gray-700">Currency</label>
                <p className="mt-1 text-sm text-gray-900">{item.currency_name} ({item.currency_code})</p>
              </div>
              
              <div>
                <label className="block text-sm font-medium text-gray-700">Balance</label>
                <p className="mt-1 text-sm text-gray-900">{item.balance} {item.currency_code}</p>
              </div>
              
              <div>
                <label className="block text-sm font-medium text-gray-700">Wallet Address</label>
                <p className="mt-1 text-sm text-gray-900 break-all">{item.wallet_address}</p>
              </div>
            </>
          )}
          
          <div>
            <label className="block text-sm font-medium text-gray-700">Status</label>
            <div className="mt-1">{getStatusBadge(item.status)}</div>
          </div>
          
          <div>
            <label className="block text-sm font-medium text-gray-700">User</label>
            <p className="mt-1 text-sm text-gray-900">{item.user.name}</p>
            <p className="text-sm text-gray-500">{item.user.email}</p>
          </div>
          
          <div>
            <label className="block text-sm font-medium text-gray-700">Created Date</label>
            <p className="mt-1 text-sm text-gray-900">{formatDate(item.created_at)}</p>
          </div>
          
          {item.executed_at && (
            <div>
              <label className="block text-sm font-medium text-gray-700">Executed Date</label>
              <p className="mt-1 text-sm text-gray-900">{formatDate(item.executed_at)}</p>
            </div>
          )}
        </div>
      </div>
    </div>
  </div>
);

// Edit Modal Component
const EditModal = ({ item, type, statuses, types, currencies, users, wallets, onClose, onSave }) => {
  const [formData, setFormData] = useState({
    // For transactions
    user_id: item?.user?.id || '',
    wallet_id: item?.wallet?.id || '',
    transaction_hash: item?.transaction_hash || '',
    type: item?.type || '',
    currency_code: item?.currency_code || '',
    crypto_amount: item?.crypto_amount || '',
    usd_amount: item?.usd_amount || '',
    price_per_unit: item?.price_per_unit || '',
    fee: item?.fee || '',
    status: item?.status || '',
    executed_at: item?.executed_at?.split('T')[0] || '',
    // For wallets
    currency_name: item?.currency_name || '',
    balance: item?.balance || '',
    wallet_address: item?.wallet_address || ''
  });

  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);

  const handleSubmit = async (e) => {
    e.preventDefault();
    setLoading(true);
    setError(null);
    
    const submitData = { ...formData };
    
    // Remove empty dates and fields that shouldn't be empty strings
    Object.keys(submitData).forEach(key => {
      if (submitData[key] === '' && (key === 'executed_at')) {
        delete submitData[key];
      }
    });
    
    try {
      await onSave(submitData);
      onClose(); // Close modal on success
    } catch (err) {
      setError(err.response?.data?.message || err.message || `Failed to update ${type}`);
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="fixed inset-0 bg-gray-600 bg-opacity-50 overflow-y-auto h-full w-full z-50">
      <div className="relative top-10 mx-auto p-5 border w-full max-w-2xl shadow-lg rounded-md bg-white">
        <div className="mt-3">
          <div className="flex items-center justify-between mb-4">
            <h3 className="text-lg font-medium text-gray-900">Edit {type === 'transaction' ? 'Transaction' : 'Wallet'}</h3>
            <button onClick={onClose} className="text-gray-400 hover:text-gray-600" disabled={loading}>
              <span className="sr-only">Close</span>
              <svg className="h-6 w-6" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M6 18L18 6M6 6l12 12" />
              </svg>
            </button>
          </div>

          {/* Error Display */}
          {error && (
            <div className="mb-4 bg-red-50 border border-red-200 text-red-700 px-4 py-3 rounded relative">
              <span className="block sm:inline">{error}</span>
              <span 
                className="absolute top-0 bottom-0 right-0 px-4 py-3 cursor-pointer"
                onClick={() => setError(null)}
              >
                <svg className="fill-current h-6 w-6 text-red-500" role="button" xmlns="http://www.w3.org/2000/svg" viewBox="0 0 20 20">
                  <title>Close</title>
                  <path d="M14.348 14.849a1.2 1.2 0 0 1-1.697 0L10 11.819l-2.651 3.029a1.2 1.2 0 1 1-1.697-1.697l2.758-3.15-2.759-3.152a1.2 1.2 0 1 1 1.697-1.697L10 8.183l2.651-3.031a1.2 1.2 0 1 1 1.697 1.697l-2.758 3.152 2.758 3.15a1.2 1.2 0 0 1 0 1.698z"/>
                </svg>
              </span>
            </div>
          )}
          
          <form onSubmit={handleSubmit} className="space-y-4 max-h-96 overflow-y-auto">
            {type === 'transaction' ? (
              <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                <div>
                  <label className="block text-sm font-medium text-gray-700">User</label>
                  <select
                    value={formData.user_id}
                    onChange={(e) => setFormData({ ...formData, user_id: e.target.value })}
                    className="mt-1 block w-full border-gray-300 rounded-md shadow-sm focus:ring-blue-500 focus:border-blue-500 sm:text-sm"
                  >
                    <option value="">Select User</option>
                    {users?.map((user) => (
                      <option key={user.id} value={user.id}>{user.name}</option>
                    ))}
                  </select>
                </div>

                <div>
                  <label className="block text-sm font-medium text-gray-700">Wallet</label>
                  <select
                    value={formData.wallet_id}
                    onChange={(e) => setFormData({ ...formData, wallet_id: e.target.value })}
                    className="mt-1 block w-full border-gray-300 rounded-md shadow-sm focus:ring-blue-500 focus:border-blue-500 sm:text-sm"
                  >
                    <option value="">Select Wallet</option>
                    {wallets?.map((wallet) => (
                      <option key={wallet.id} value={wallet.id}>
                        {wallet.currency_name} ({wallet.currency_code}) - {wallet.wallet_address?.substr(0, 10)}...
                      </option>
                    ))}
                  </select>
                </div>

                <div>
                  <label className="block text-sm font-medium text-gray-700">Transaction Hash</label>
                  <input
                    type="text"
                    value={formData.transaction_hash}
                    onChange={(e) => setFormData({ ...formData, transaction_hash: e.target.value })}
                    className="mt-1 block w-full border-gray-300 rounded-md shadow-sm focus:ring-blue-500 focus:border-blue-500 sm:text-sm"
                  />
                </div>

                <div>
                  <label className="block text-sm font-medium text-gray-700">Type</label>
                  <select
                    value={formData.type}
                    onChange={(e) => setFormData({ ...formData, type: e.target.value })}
                    className="mt-1 block w-full border-gray-300 rounded-md shadow-sm focus:ring-blue-500 focus:border-blue-500 sm:text-sm"
                  >
                    <option value="">Select Type</option>
                    {Object.entries(types || {}).map(([key, value]) => (
                      <option key={key} value={key}>{value}</option>
                    ))}
                  </select>
                </div>

                <div>
                  <label className="block text-sm font-medium text-gray-700">Currency</label>
                  <select
                    value={formData.currency_code}
                    onChange={(e) => setFormData({ ...formData, currency_code: e.target.value })}
                    className="mt-1 block w-full border-gray-300 rounded-md shadow-sm focus:ring-blue-500 focus:border-blue-500 sm:text-sm"
                  >
                    <option value="">Select Currency</option>
                    {Object.entries(currencies || {}).map(([key, value]) => (
                      <option key={key} value={key}>{value} ({key})</option>
                    ))}
                  </select>
                </div>

                <div>
                  <label className="block text-sm font-medium text-gray-700">Crypto Amount</label>
                  <input
                    type="number"
                    step="0.00000001"
                    min="0"
                    value={formData.crypto_amount}
                    onChange={(e) => setFormData({ ...formData, crypto_amount: e.target.value })}
                    className="mt-1 block w-full border-gray-300 rounded-md shadow-sm focus:ring-blue-500 focus:border-blue-500 sm:text-sm"
                  />
                </div>

                <div>
                  <label className="block text-sm font-medium text-gray-700">USD Amount</label>
                  <input
                    type="number"
                    step="0.01"
                    min="0"
                    value={formData.usd_amount}
                    onChange={(e) => setFormData({ ...formData, usd_amount: e.target.value })}
                    className="mt-1 block w-full border-gray-300 rounded-md shadow-sm focus:ring-blue-500 focus:border-blue-500 sm:text-sm"
                  />
                </div>

                <div>
                  <label className="block text-sm font-medium text-gray-700">Price per Unit</label>
                  <input
                    type="number"
                    step="0.01"
                    min="0"
                    value={formData.price_per_unit}
                    onChange={(e) => setFormData({ ...formData, price_per_unit: e.target.value })}
                    className="mt-1 block w-full border-gray-300 rounded-md shadow-sm focus:ring-blue-500 focus:border-blue-500 sm:text-sm"
                  />
                </div>

                <div>
                  <label className="block text-sm font-medium text-gray-700">Fee</label>
                  <input
                    type="number"
                    step="0.01"
                    min="0"
                    value={formData.fee}
                    onChange={(e) => setFormData({ ...formData, fee: e.target.value })}
                    className="mt-1 block w-full border-gray-300 rounded-md shadow-sm focus:ring-blue-500 focus:border-blue-500 sm:text-sm"
                  />
                </div>

                <div>
                  <label className="block text-sm font-medium text-gray-700">Status</label>
                  <select
                    value={formData.status}
                    onChange={(e) => setFormData({ ...formData, status: e.target.value })}
                    className="mt-1 block w-full border-gray-300 rounded-md shadow-sm focus:ring-blue-500 focus:border-blue-500 sm:text-sm"
                  >
                    {Object.entries(statuses || {}).map(([key, value]) => (
                      <option key={key} value={key}>{value}</option>
                    ))}
                  </select>
                </div>

                <div>
                  <label className="block text-sm font-medium text-gray-700">Executed Date</label>
                  <input
                    type="date"
                    value={formData.executed_at}
                    onChange={(e) => setFormData({ ...formData, executed_at: e.target.value })}
                    className="mt-1 block w-full border-gray-300 rounded-md shadow-sm focus:ring-blue-500 focus:border-blue-500 sm:text-sm"
                  />
                </div>
              </div>
            ) : (
              <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                <div>
                  <label className="block text-sm font-medium text-gray-700">Currency Name</label>
                  <input
                    type="text"
                    value={formData.currency_name}
                    onChange={(e) => setFormData({ ...formData, currency_name: e.target.value })}
                    className="mt-1 block w-full border-gray-300 rounded-md shadow-sm focus:ring-blue-500 focus:border-blue-500 sm:text-sm"
                  />
                </div>

                <div>
                  <label className="block text-sm font-medium text-gray-700">Currency Code</label>
                  <input
                    type="text"
                    value={formData.currency_code}
                    onChange={(e) => setFormData({ ...formData, currency_code: e.target.value })}
                    className="mt-1 block w-full border-gray-300 rounded-md shadow-sm focus:ring-blue-500 focus:border-blue-500 sm:text-sm"
                  />
                </div>

                <div>
                  <label className="block text-sm font-medium text-gray-700">Balance</label>
                  <input
                    type="number"
                    step="0.00000001"
                    min="0"
                    value={formData.balance}
                    onChange={(e) => setFormData({ ...formData, balance: e.target.value })}
                    className="mt-1 block w-full border-gray-300 rounded-md shadow-sm focus:ring-blue-500 focus:border-blue-500 sm:text-sm"
                  />
                </div>

                <div>
                  <label className="block text-sm font-medium text-gray-700">Status</label>
                  <select
                    value={formData.status}
                    onChange={(e) => setFormData({ ...formData, status: e.target.value })}
                    className="mt-1 block w-full border-gray-300 rounded-md shadow-sm focus:ring-blue-500 focus:border-blue-500 sm:text-sm"
                  >
                    {Object.entries(statuses || {}).map(([key, value]) => (
                      <option key={key} value={key}>{value}</option>
                    ))}
                  </select>
                </div>

                <div className="md:col-span-2">
                  <label className="block text-sm font-medium text-gray-700">Wallet Address</label>
                  <input
                    type="text"
                    value={formData.wallet_address}
                    onChange={(e) => setFormData({ ...formData, wallet_address: e.target.value })}
                    className="mt-1 block w-full border-gray-300 rounded-md shadow-sm focus:ring-blue-500 focus:border-blue-500 sm:text-sm"
                  />
                </div>
              </div>
            )}

            <div className="flex space-x-3 pt-4">
              <button
                type="submit"
                disabled={loading}
                className="flex-1 bg-blue-600 hover:bg-blue-700 disabled:opacity-50 disabled:cursor-not-allowed text-white font-medium py-2 px-4 rounded-md flex items-center justify-center"
              >
                {loading ? (
                  <>
                    <svg className="animate-spin -ml-1 mr-3 h-5 w-5 text-white" xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24">
                      <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4"></circle>
                      <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"></path>
                    </svg>
                    Updating...
                  </>
                ) : (
                  `Update ${type === 'transaction' ? 'Transaction' : 'Wallet'}`
                )}
              </button>
              <button
                type="button"
                onClick={onClose}
                disabled={loading}
                className="flex-1 bg-gray-300 hover:bg-gray-400 disabled:opacity-50 disabled:cursor-not-allowed text-gray-700 font-medium py-2 px-4 rounded-md"
              >
                Cancel
              </button>
            </div>
          </form>
        </div>
      </div>
    </div>
  );
};

// Confirmation Modal Component
const ConfirmationModal = ({ title, message, confirmText, confirmClass, onConfirm, onCancel }) => (
  <div className="fixed inset-0 bg-gray-600 bg-opacity-50 overflow-y-auto h-full w-full z-50">
    <div className="relative top-20 mx-auto p-5 border w-96 shadow-lg rounded-md bg-white">
      <div className="mt-3 text-center">
        <div className="mx-auto flex items-center justify-center h-12 w-12 rounded-full bg-red-100 mb-4">
          <svg className="h-6 w-6 text-red-600" fill="none" viewBox="0 0 24 24" stroke="currentColor">
            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M12 9v2m0 4h.01m-6.938 4h13.856c1.54 0 2.502-1.667 1.732-2.5L13.732 4c-.77-.833-1.732-.833-2.5 0L4.268 18.5c-.77.833.192 2.5 1.732 2.5z" />
          </svg>
        </div>
        <h3 className="text-lg font-medium text-gray-900 mb-2">{title}</h3>
        <p className="text-sm text-gray-500 mb-4">{message}</p>
        <div className="flex space-x-3">
          <button
            onClick={onConfirm}
            className={`flex-1 text-white font-medium py-2 px-4 rounded-md ${confirmClass}`}
          >
            {confirmText}
          </button>
          <button
            onClick={onCancel}
            className="flex-1 bg-gray-300 hover:bg-gray-400 text-gray-700 font-medium py-2 px-4 rounded-md"
          >
            Cancel
          </button>
        </div>
      </div>
    </div>
  </div>
);

// Create Transaction Modal Component
const CreateTransactionModal = ({ users, wallets, statuses, types, currencies, onClose, onSave }) => {
  const [formData, setFormData] = useState({
    user_id: '',
    wallet_id: '',
    transaction_hash: '',
    type: '',
    currency_code: '',
    crypto_amount: '',
    usd_amount: '',
    price_per_unit: '',
    fee: '',
    status: 'pending',
    executed_at: ''
  });

  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);

  const handleSubmit = async (e) => {
    e.preventDefault();
    setLoading(true);
    setError(null);
    
    const submitData = { ...formData };
    
    // Remove empty dates and fields that shouldn't be empty strings
    Object.keys(submitData).forEach(key => {
      if (submitData[key] === '' && (key === 'executed_at' || key === 'transaction_hash')) {
        delete submitData[key];
      }
    });
    
    try {
      await onSave(submitData);
      onClose(); // Close modal on success
    } catch (err) {
      setError(err.response?.data?.message || err.message || 'Failed to create transaction');
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="fixed inset-0 bg-gray-600 bg-opacity-50 overflow-y-auto h-full w-full z-50">
      <div className="relative top-10 mx-auto p-5 border w-full max-w-2xl shadow-lg rounded-md bg-white">
        <div className="mt-3">
          <div className="flex items-center justify-between mb-4">
            <h3 className="text-lg font-medium text-gray-900">Create New Transaction</h3>
            <button onClick={onClose} className="text-gray-400 hover:text-gray-600" disabled={loading}>
              <span className="sr-only">Close</span>
              <svg className="h-6 w-6" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M6 18L18 6M6 6l12 12" />
              </svg>
            </button>
          </div>

          {/* Error Display */}
          {error && (
            <div className="mb-4 bg-red-50 border border-red-200 text-red-700 px-4 py-3 rounded relative">
              <span className="block sm:inline">{error}</span>
              <span 
                className="absolute top-0 bottom-0 right-0 px-4 py-3 cursor-pointer"
                onClick={() => setError(null)}
              >
                <svg className="fill-current h-6 w-6 text-red-500" role="button" xmlns="http://www.w3.org/2000/svg" viewBox="0 0 20 20">
                  <title>Close</title>
                  <path d="M14.348 14.849a1.2 1.2 0 0 1-1.697 0L10 11.819l-2.651 3.029a1.2 1.2 0 1 1-1.697-1.697l2.758-3.15-2.759-3.152a1.2 1.2 0 1 1 1.697-1.697L10 8.183l2.651-3.031a1.2 1.2 0 1 1 1.697 1.697l-2.758 3.152 2.758 3.15a1.2 1.2 0 0 1 0 1.698z"/>
                </svg>
              </span>
            </div>
          )}
          
          <form onSubmit={handleSubmit} className="space-y-4 max-h-96 overflow-y-auto">
            <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
              <div>
                <label className="block text-sm font-medium text-gray-700">User *</label>
                <select
                  value={formData.user_id}
                  onChange={(e) => setFormData({ ...formData, user_id: e.target.value })}
                  required
                  className="mt-1 block w-full border-gray-300 rounded-md shadow-sm focus:ring-blue-500 focus:border-blue-500 sm:text-sm"
                >
                  <option value="">Select User</option>
                  {users?.map((user) => (
                    <option key={user.id} value={user.id}>{user.name}</option>
                  ))}
                </select>
              </div>

              <div>
                <label className="block text-sm font-medium text-gray-700">Wallet *</label>
                <select
                  value={formData.wallet_id}
                  onChange={(e) => setFormData({ ...formData, wallet_id: e.target.value })}
                  required
                  className="mt-1 block w-full border-gray-300 rounded-md shadow-sm focus:ring-blue-500 focus:border-blue-500 sm:text-sm"
                >
                  <option value="">Select Wallet</option>
                  {wallets?.map((wallet) => (
                    <option key={wallet.id} value={wallet.id}>
                      {wallet.currency_name} ({wallet.currency_code}) - {wallet.wallet_address?.substr(0, 10)}...
                    </option>
                  ))}
                </select>
              </div>

              <div>
                <label className="block text-sm font-medium text-gray-700">Transaction Hash</label>
                <input
                  type="text"
                  value={formData.transaction_hash}
                  onChange={(e) => setFormData({ ...formData, transaction_hash: e.target.value })}
                  placeholder="Leave empty to auto-generate"
                  className="mt-1 block w-full border-gray-300 rounded-md shadow-sm focus:ring-blue-500 focus:border-blue-500 sm:text-sm"
                />
              </div>

              <div>
                <label className="block text-sm font-medium text-gray-700">Type *</label>
                <select
                  value={formData.type}
                  onChange={(e) => setFormData({ ...formData, type: e.target.value })}
                  required
                  className="mt-1 block w-full border-gray-300 rounded-md shadow-sm focus:ring-blue-500 focus:border-blue-500 sm:text-sm"
                >
                  <option value="">Select Type</option>
                  {Object.entries(types || {}).map(([key, value]) => (
                    <option key={key} value={key}>{value}</option>
                  ))}
                </select>
              </div>

              <div>
                <label className="block text-sm font-medium text-gray-700">Currency *</label>
                <select
                  value={formData.currency_code}
                  onChange={(e) => setFormData({ ...formData, currency_code: e.target.value })}
                  required
                  className="mt-1 block w-full border-gray-300 rounded-md shadow-sm focus:ring-blue-500 focus:border-blue-500 sm:text-sm"
                >
                  <option value="">Select Currency</option>
                  {Object.entries(currencies || {}).map(([key, value]) => (
                    <option key={key} value={key}>{value} ({key})</option>
                  ))}
                </select>
              </div>

              <div>
                <label className="block text-sm font-medium text-gray-700">Crypto Amount *</label>
                <input
                  type="number"
                  step="0.00000001"
                  min="0"
                  value={formData.crypto_amount}
                  onChange={(e) => setFormData({ ...formData, crypto_amount: e.target.value })}
                  required
                  className="mt-1 block w-full border-gray-300 rounded-md shadow-sm focus:ring-blue-500 focus:border-blue-500 sm:text-sm"
                />
              </div>

              <div>
                <label className="block text-sm font-medium text-gray-700">USD Amount *</label>
                <input
                  type="number"
                  step="0.01"
                  min="0"
                  value={formData.usd_amount}
                  onChange={(e) => setFormData({ ...formData, usd_amount: e.target.value })}
                  required
                  className="mt-1 block w-full border-gray-300 rounded-md shadow-sm focus:ring-blue-500 focus:border-blue-500 sm:text-sm"
                />
              </div>

              <div>
                <label className="block text-sm font-medium text-gray-700">Price per Unit</label>
                <input
                  type="number"
                  step="0.01"
                  min="0"
                  value={formData.price_per_unit}
                  onChange={(e) => setFormData({ ...formData, price_per_unit: e.target.value })}
                  placeholder="Auto-calculated if empty"
                  className="mt-1 block w-full border-gray-300 rounded-md shadow-sm focus:ring-blue-500 focus:border-blue-500 sm:text-sm"
                />
              </div>

              <div>
                <label className="block text-sm font-medium text-gray-700">Fee</label>
                <input
                  type="number"
                  step="0.01"
                  min="0"
                  value={formData.fee}
                  onChange={(e) => setFormData({ ...formData, fee: e.target.value })}
                  placeholder="0.00"
                  className="mt-1 block w-full border-gray-300 rounded-md shadow-sm focus:ring-blue-500 focus:border-blue-500 sm:text-sm"
                />
              </div>

              <div>
                <label className="block text-sm font-medium text-gray-700">Status</label>
                <select
                  value={formData.status}
                  onChange={(e) => setFormData({ ...formData, status: e.target.value })}
                  className="mt-1 block w-full border-gray-300 rounded-md shadow-sm focus:ring-blue-500 focus:border-blue-500 sm:text-sm"
                >
                  {Object.entries(statuses || {}).map(([key, value]) => (
                    <option key={key} value={key}>{value}</option>
                  ))}
                </select>
              </div>

              <div>
                <label className="block text-sm font-medium text-gray-700">Executed Date</label>
                <input
                  type="date"
                  value={formData.executed_at}
                  onChange={(e) => setFormData({ ...formData, executed_at: e.target.value })}
                  className="mt-1 block w-full border-gray-300 rounded-md shadow-sm focus:ring-blue-500 focus:border-blue-500 sm:text-sm"
                />
              </div>
            </div>

            <div className="flex space-x-3 pt-4">
              <button
                type="submit"
                disabled={loading}
                className="flex-1 bg-blue-600 hover:bg-blue-700 disabled:opacity-50 disabled:cursor-not-allowed text-white font-medium py-2 px-4 rounded-md flex items-center justify-center"
              >
                {loading ? (
                  <>
                    <svg className="animate-spin -ml-1 mr-3 h-5 w-5 text-white" xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24">
                      <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4"></circle>
                      <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"></path>
                    </svg>
                    Creating...
                  </>
                ) : (
                  'Create Transaction'
                )}
              </button>
              <button
                type="button"
                onClick={onClose}
                disabled={loading}
                className="flex-1 bg-gray-300 hover:bg-gray-400 disabled:opacity-50 disabled:cursor-not-allowed text-gray-700 font-medium py-2 px-4 rounded-md"
              >
                Cancel
              </button>
            </div>
          </form>
        </div>
      </div>
    </div>
  );
};

// Create Wallet Modal Component
const CreateWalletModal = ({ users, currencies, walletStatuses, onClose, onSave }) => {
  const [formData, setFormData] = useState({
    user_id: '',
    currency_code: '',
    currency_name: '',
    balance: '0',
    status: 'active',
    wallet_address: ''
  });

  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);

  const handleSubmit = async (e) => {
    e.preventDefault();
    setLoading(true);
    setError(null);
    
    const submitData = { ...formData };
    
    // Auto-set currency_name based on currency_code
    if (submitData.currency_code && currencies[submitData.currency_code]) {
      submitData.currency_name = currencies[submitData.currency_code];
    }
    
    try {
      await onSave(submitData);
      onClose(); // Close modal on success
    } catch (err) {
      setError(err.response?.data?.message || err.message || 'Failed to create wallet');
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="fixed inset-0 bg-gray-600 bg-opacity-50 overflow-y-auto h-full w-full z-50">
      <div className="relative top-10 mx-auto p-5 border w-full max-w-2xl shadow-lg rounded-md bg-white">
        <div className="mt-3">
          <div className="flex items-center justify-between mb-4">
            <h3 className="text-lg font-medium text-gray-900">Create New Wallet</h3>
            <button onClick={onClose} className="text-gray-400 hover:text-gray-600" disabled={loading}>
              <span className="sr-only">Close</span>
              <svg className="h-6 w-6" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M6 18L18 6M6 6l12 12" />
              </svg>
            </button>
          </div>

          {/* Error Display */}
          {error && (
            <div className="mb-4 bg-red-50 border border-red-200 text-red-700 px-4 py-3 rounded relative">
              <span className="block sm:inline">{error}</span>
              <span 
                className="absolute top-0 bottom-0 right-0 px-4 py-3 cursor-pointer"
                onClick={() => setError(null)}
              >
                <svg className="fill-current h-6 w-6 text-red-500" role="button" xmlns="http://www.w3.org/2000/svg" viewBox="0 0 20 20">
                  <title>Close</title>
                  <path d="M14.348 14.849a1.2 1.2 0 0 1-1.697 0L10 11.819l-2.651 3.029a1.2 1.2 0 1 1-1.697-1.697l2.758-3.15-2.759-3.152a1.2 1.2 0 1 1 1.697-1.697L10 8.183l2.651-3.031a1.2 1.2 0 1 1 1.697 1.697l-2.758 3.152 2.758 3.15a1.2 1.2 0 0 1 0 1.698z"/>
                </svg>
              </span>
            </div>
          )}
          
          <form onSubmit={handleSubmit} className="space-y-4 max-h-96 overflow-y-auto">
            <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
              <div>
                <label className="block text-sm font-medium text-gray-700">User *</label>
                <select
                  value={formData.user_id}
                  onChange={(e) => setFormData({ ...formData, user_id: e.target.value })}
                  required
                  className="mt-1 block w-full border-gray-300 rounded-md shadow-sm focus:ring-blue-500 focus:border-blue-500 sm:text-sm"
                >
                  <option value="">Select User</option>
                  {users?.map((user) => (
                    <option key={user.id} value={user.id}>{user.name}</option>
                  ))}
                </select>
              </div>

              <div>
                <label className="block text-sm font-medium text-gray-700">Currency *</label>
                <select
                  value={formData.currency_code}
                  onChange={(e) => setFormData({ ...formData, currency_code: e.target.value })}
                  required
                  className="mt-1 block w-full border-gray-300 rounded-md shadow-sm focus:ring-blue-500 focus:border-blue-500 sm:text-sm"
                >
                  <option value="">Select Currency</option>
                  {Object.entries(currencies || {}).map(([key, value]) => (
                    <option key={key} value={key}>{value} ({key})</option>
                  ))}
                </select>
              </div>

              <div>
                <label className="block text-sm font-medium text-gray-700">Currency Name</label>
                <input
                  type="text"
                  value={formData.currency_name}
                  onChange={(e) => setFormData({ ...formData, currency_name: e.target.value })}
                  className="mt-1 block w-full border-gray-300 rounded-md shadow-sm focus:ring-blue-500 focus:border-blue-500 sm:text-sm"
                />
              </div>

              <div>
                <label className="block text-sm font-medium text-gray-700">Balance *</label>
                <input
                  type="number"
                  step="0.00000001"
                  min="0"
                  value={formData.balance}
                  onChange={(e) => setFormData({ ...formData, balance: e.target.value })}
                  required
                  className="mt-1 block w-full border-gray-300 rounded-md shadow-sm focus:ring-blue-500 focus:border-blue-500 sm:text-sm"
                />
              </div>

              <div>
                <label className="block text-sm font-medium text-gray-700">Status</label>
                <select
                  value={formData.status}
                  onChange={(e) => setFormData({ ...formData, status: e.target.value })}
                  className="mt-1 block w-full border-gray-300 rounded-md shadow-sm focus:ring-blue-500 focus:border-blue-500 sm:text-sm"
                >
                  {Object.entries(walletStatuses || {}).map(([key, value]) => (
                    <option key={key} value={key}>{value}</option>
                  ))}
                </select>
              </div>

              <div className="md:col-span-2">
                <label className="block text-sm font-medium text-gray-700">Wallet Address</label>
                <input
                  type="text"
                  value={formData.wallet_address}
                  onChange={(e) => setFormData({ ...formData, wallet_address: e.target.value })}
                  className="mt-1 block w-full border-gray-300 rounded-md shadow-sm focus:ring-blue-500 focus:border-blue-500 sm:text-sm"
                />
              </div>
            </div>

            <div className="flex space-x-3 pt-4">
              <button
                type="submit"
                disabled={loading}
                className="flex-1 bg-blue-600 hover:bg-blue-700 disabled:opacity-50 disabled:cursor-not-allowed text-white font-medium py-2 px-4 rounded-md flex items-center justify-center"
              >
                {loading ? (
                  <>
                    <svg className="animate-spin -ml-1 mr-3 h-5 w-5 text-white" xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24">
                      <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4"></circle>
                      <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"></path>
                    </svg>
                    Creating...
                  </>
                ) : (
                  'Create Wallet'
                )}
              </button>
              <button
                type="button"
                onClick={onClose}
                disabled={loading}
                className="flex-1 bg-gray-300 hover:bg-gray-400 disabled:opacity-50 disabled:cursor-not-allowed text-gray-700 font-medium py-2 px-4 rounded-md"
              >
                Cancel
              </button>
            </div>
          </form>
        </div>
      </div>
    </div>
  );
};

export default CryptoManagement; 